import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { DraggableBlockPlugin_EXPERIMENTAL } from '@lexical/react/LexicalDraggableBlockPlugin';
import { $createParagraphNode, $getNearestNodeFromDOMNode, $getSelection, $isRangeSelection, $getRoot, $getNodeByKey, $isDecoratorNode, $createNodeSelection, $setSelection } from 'lexical';
import { useEffect, useRef, useState } from 'react';
import Add from '../../icons/Add';
import DragIndicator from '../../icons/DragIndicator';
import useHandlePopoverClick from '../../../hooks/useHandlePopoverClick';
import { setPageEditor } from '../utils/editorStore';
import './scss/DraggableBlock.scss';
import './scss/BlockActionMenu.scss';

const DRAGGABLE_BLOCK_MENU_CLASSNAME = 'draggable-block-menu';

function isOnMenu(element) {
  return !!element.closest(`.${DRAGGABLE_BLOCK_MENU_CLASSNAME}`);
}

export default function DraggableBlockPlugin({ anchorElem = document.body }) {
  const [editor] = useLexicalComposerContext();
  const menuRef = useRef(null);
  const targetLineRef = useRef(null);
  const [draggableElement, setDraggableElement] = useState(null);
  const [computedAnchor, setComputedAnchor] = useState(null);
  const targetTopLevelKeyRef = useRef(null);
  const [currentBlockKey, setCurrentBlockKey] = useState(null);
  const selectedBlockElemRef = useRef(null);
  const { handlePopoverClick, popoverState } = useHandlePopoverClick();


  useEffect(() => {
    const root = editor.getRootElement();
    // Prefer the editor container (parent of contentEditable) to avoid positioning outside
    setComputedAnchor(root?.parentElement ?? document.body);
    // Store editor ref for popovers rendered outside LexicalComposer tree
    setPageEditor(editor);
  }, [editor]);

  // Keep drag handle visible AND in-place while popover is open.
  // Lexical's plugin sets transform:translate(-10000px,-10000px) when the
  // mouse leaves the editor area, which moves the handle off-screen and
  // causes usePopoverPositionUpdater to reposition the popover off-screen too.
  useEffect(() => {
    const isBlockActionsOpen = popoverState.popovers?.some(p => p.contentId === 'blockActions');
    const menu = menuRef.current;

    if (isBlockActionsOpen && menu) {
      menu.classList.add('popover-open');
      // Snapshot the current (valid) transform so we can restore it
      const savedTransform = menu.style.transform;

      const observer = new MutationObserver(() => {
        if (menu.style.transform?.includes('-10000')) {
          menu.style.transform = savedTransform;
        }
      });
      observer.observe(menu, { attributes: true, attributeFilter: ['style'] });

      return () => observer.disconnect();
    } else {
      if (menu) menu.classList.remove('popover-open');
      if (selectedBlockElemRef.current) {
        selectedBlockElemRef.current.classList.remove('block-selected');
        selectedBlockElemRef.current = null;
      }
    }
  }, [popoverState]);

  useEffect(() => {
    if (!draggableElement) {
      targetTopLevelKeyRef.current = null;
      return;
    }
    editor.getEditorState().read(() => {
      const node = $getNearestNodeFromDOMNode(draggableElement);
      let topLevel = null;
      try {
        topLevel = node?.getTopLevelElementOrThrow();
      } catch (_) {
        topLevel = null;
      }
      targetTopLevelKeyRef.current = topLevel ? topLevel.getKey() : null;
    });
  }, [draggableElement, editor]);

  // useEffect(() => {
  //   if (!draggableElement) {
  //     targetTopLevelKeyRef.current = null;
  //     setCurrentBlockKey(null);
  //     return;
  //   }
  //   editor.getEditorState().read(() => {
  //     const node = $getNearestNodeFromDOMNode(draggableElement);
  //     let topLevel = null;
  //     try {
  //       topLevel = node?.getTopLevelElementOrThrow();
  //     } catch (_) {
  //       topLevel = null;
  //     }
  //     const key = topLevel ? topLevel.getKey() : null;
  //     targetTopLevelKeyRef.current = key;
  //     setCurrentBlockKey(key);
  //   });
  // }, [draggableElement, editor]);


  function insertBlock(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!editor) return;

    // Ensure editor receives focus so typing works immediately
    editor.focus();

    editor.update(() => {
      const root = $getRoot();
      const pNode = $createParagraphNode();

      // Resolve the anchor block to insert after
      let topLevel = null;

      // 1) Use the stored top-level key captured from hover
      const storedKey = targetTopLevelKeyRef.current;
      if (storedKey) {
        const n = $getNodeByKey(storedKey);
        if (n) {
          try {
            topLevel = n.getTopLevelElementOrThrow();
          } catch (_) {
            topLevel = null;
          }
        }
      }

      // 2) Fallback: derive from current draggableElement
      if (!topLevel && draggableElement) {
        const n = $getNearestNodeFromDOMNode(draggableElement);
        if (n) {
          try {
            topLevel = n.getTopLevelElementOrThrow();
          } catch (_) {
            topLevel = null;
          }
        }
      }

      // 3) Fallback: derive from current selection
      if (!topLevel) {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const n = selection.anchor.getNode();
          if (n) {
            try {
              topLevel = n.getTopLevelElementOrThrow();
            } catch (_) {
              topLevel = null;
            }
          }
        }
      }

      if (topLevel && topLevel.getKey() !== root.getKey()) {
        topLevel.insertAfter(pNode);
      } else if (root.getLastChild()) {
        // Append after the last top-level if we still couldn't resolve
        root.getLastChild().insertAfter(pNode);
      } else {
        // Empty doc
        root.append(pNode);
      }

      pNode.select();
    });
  }

  function getBlockAtY(y) {
    const rootElement = editor.getRootElement();
    if (!rootElement) return null;

    for (const child of rootElement.children) {
      const rect = child.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) {
        let result = null;
        editor.read(() => {
          const node = $getNearestNodeFromDOMNode(child);
          const topLevel = node?.getTopLevelElement();
          if (topLevel) {
            result = { key: topLevel.getKey(), type: topLevel.getType() };
          }
        });
        return result;
      }
    }
    return null;
  }

  function onHandleClick(e, triggerEl) {
    e.preventDefault();
    e.stopPropagation();

    const block = getBlockAtY(triggerEl.getBoundingClientRect().top);
    if (!block) return;

    const { key: blockNodeKey, type: blockType } = block;

    // Select the block so actions like "Turn into" work
    editor.update(() => {
      const node = $getNodeByKey(blockNodeKey);
      if (!node) return;
      if ($isDecoratorNode(node)) {
        const selection = $createNodeSelection();
        selection.add(blockNodeKey);
        $setSelection(selection);
      } else {
        node.selectStart();
      }
    });

    // Add visual highlight to the block
    if (selectedBlockElemRef.current) {
      selectedBlockElemRef.current.classList.remove('block-selected');
    }
    const blockElem = editor.getElementByKey(blockNodeKey);
    if (!blockElem) return;

    blockElem.classList.add('block-selected');
    selectedBlockElemRef.current = blockElem;

    // Stamp a stable id on the trigger so handlePopoverClick can anchor to it
    triggerEl.id = `block-handle-${blockNodeKey}`;

    // Use handlePopoverClick — positions popover below the drag handle
    handlePopoverClick(
      { currentTarget: triggerEl },
      'blockActions',
      { blockNodeKey, blockType }
    );
  }

  return (
    <DraggableBlockPlugin_EXPERIMENTAL
      anchorElem={computedAnchor || anchorElem}
      menuRef={menuRef}
      targetLineRef={targetLineRef}
      menuComponent={
        <div ref={menuRef} className="draggable-block-menu">
          <button
            type="button"
            title="Add block"
            className="menu-btn plus"
            onMouseDown={insertBlock}
          >
            <Add size={16} />
          </button>
          <button
            type="button"
            className="menu-btn handle"
            title="Drag block or click for menu"
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              const triggerEl = e.currentTarget;
              let isDrag = false;

              const onMove = () => {
                isDrag = true;
                cleanup();
              };
              const onUp = (upEvt) => {
                cleanup();
                if (!isDrag) onHandleClick(upEvt, triggerEl);
              };
              const cleanup = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
              };
              window.addEventListener('mousemove', onMove, { once: false });
              window.addEventListener('mouseup', onUp, { once: true });
            }}
            data-popover-trigger="blockActions"
            id={`block-handle-${currentBlockKey || 'unknown'}`}

          >
            <DragIndicator size={16} />
          </button>
        </div>
      }
      targetLineComponent={
        <div ref={targetLineRef} className="draggable-block-target-line" />
      }
      isOnMenu={isOnMenu}
      onElementChanged={setDraggableElement}
    />
  );
}

